import type { ApiClient } from '@/api/client'
import {
  ACTION_DEFINITION_LIST,
  TRIGGER_DEFINITION_LIST,
  type ActionDefinition,
  type ActivityEntry,
  type AutomationStatus,
  type ScheduledAction,
  type TriggerDefinition,
  type Workflow,
} from '@/api/automation'
import {
  type ReportDataset,
  type ReportExport,
  type ReportInput,
  type ReportRow,
  type ReportSchedule,
  type RequestExportInput,
  type SavedReport,
} from '@/api/reports'
import type {
  CompetitorInput,
  Competitor as MarketCompetitor,
  MarketActivitySummary,
  MarketObservation,
  MarketPricingSummary,
  MonitoringStatus,
  PrizeTrendPoint,
  TrackedCompetition,
  TrackedCompetitionInput,
  CsvImportResult,
} from '@/api/market'
import type {
  FieldGroupKey,
  Listing,
  ListingInput,
  ListingTemplate,
  ListingTemplateInput,
  PublishTargetKey,
} from '@/api/listings'
import type {
  CampaignCreative,
  CampaignDetail,
  CampaignDraft,
  CampaignListItem,
  CampaignPatch,
  CampaignPerformance,
  CampaignWebsite,
  ChannelPatch,
  CreativePatch,
  SaveIntent,
  TrackingGenerateInput,
  TrackingPatch,
} from '@/api/campaigns'
import {
  MODULE_KEYS,
  PERMISSIONS,
  ROLE_LABELS,
  type Alert,
  type AlertState,
  type AutomationSummary,
  type DailyMetrics,
  type DataMeta,
  type DigestQuery,
  type DigestReport,
  type DigestVersion,
  type DigestPeriod,
  type Entitlements,
  type Envelope,
  type Environment,
  type EnvironmentSelector,
  type FilterOptions,
  type FunnelStage,
  type HealthStatus,
  type Impersonation,
  type ModuleKey,
  type AttributionModel,
  type PeriodInfo,
  type Permission,
  type Recommendation,
  type RevenueTrend,
  type SessionUser,
  type TenantSummary,
  type Trend,
  type TrendWindow,
  type AdvertisingQuery,
  type AdvertisingReport,
  type Ga4ImportRun,
  type Ga4Integration,
  type Ga4Property,
  type Ga4StreamCheck,
  type Ga4TrafficQuery,
  type Ga4TrafficReport,
  type MetaAdAccountList,
  type MetaAdAccountMapping,
  type MetaIntegrationState,
  type MetaMappingResult,
  type WebsiteHealth,
  type WebsiteHealthStatus,
} from '@/api/types'
import { MockUsersBackend } from '@/features/settings/users/mock'
import type { UsersBackend } from '@/features/settings/users/api'
import { MockDomainsBackend } from '@/features/settings/domains/mock'
import type { DomainsBackend } from '@/features/settings/domains/api'
import { MockSiteCredentialsBackend } from '@/features/settings/websites/mock'
import type { SiteCredentialsBackend } from '@/features/settings/websites/api'
import type { SiteCredential } from '@/features/settings/websites/types'
import type { MetaConnectStart } from '@/api/integrations'
import { advertisingStoreFor, type MockAdvertisingStore } from '@/features/analytics/advertising/mock'
import { advertisingOverviewStoreFor, type MockAdvertisingOverviewStore } from '@/features/marketing/advertising/mock'
import type {
  AdAttributionReconciliation,
  AdOverviewQuery,
  AdOverviewTrendQuery,
  AdReconciliationQuery,
  AdvertisingOverview,
  AdvertisingOverviewTrend,
} from '@/api/advertisingOverview'
import { MockRecoveryStore } from '@/features/analytics/abandonment/mock'
import type {
  RecoverableCheckoutsList,
  RecoverableCheckoutsSummary,
  RecoveryQuery,
} from '@/api/recovery'
import { MockCompetitionsStore } from '@/features/analytics/competitions/mock'
import { MockCompetitionDetailStore } from '@/features/analytics/competitionDetail/mock'
import type {
  CompetitionAdvertising,
  CompetitionDetail,
  CompetitionDetailQuery,
  CompetitionDevices,
  CompetitionFunnel,
  CompetitionSources,
  SalesGranularity,
} from '@/api/competitionDetail'
import { DEFAULT_SOURCES_MODEL } from '@/api/competitionDetail'
import type { CompetitionsList, CompetitionsQuery } from '@/api/competitions'
import type { CsvFile } from '@/api/list'
import {
  leakageFromWire,
  lifecycleFromWire,
  ordersFromWire,
  type LeakageQuery,
  type LifecycleQuery,
  type OrdersQuery,
  type RevenueLeakage,
  type RevenueLifecycle,
  type RevenueOrders,
} from '@/api/revenueLeakage'
import { MockCustomersStore } from '@/features/analytics/customers/mock'
import type { CustomerReveal, CustomerSearch, CustomersList, CustomersQuery } from '@/api/customers'
import type { SignupsReport } from '@/api/signups'
import { metaStoreFor, type MockMetaStore } from '@/features/integrations/mock'
import { ga4StoreFor, type MockGa4Store } from '@/features/integrations/ga4/mock'
import { demoFirstPartyTraffic, demoFirstPartyTrafficCsv } from '@/features/analytics/traffic/mock'
import type { FirstPartyTrafficQuery, FirstPartyTrafficReport } from '@/api/firstPartyTraffic'
import type { TrackingBaseline, TrackingBaselineInput } from '@/api/dataQuality'
import { demoAttribution } from '@/features/marketing/attribution/mock'
import type { AttributionQuery, AttributionReport } from '@/api/attribution'
import { ApiError } from '@/lib/api'
import { ENVIRONMENT_LABELS, isNonProduction } from '@/lib/environment'
import { addDays, DEFAULT_FILTERS, eachDay, resolvePeriod, toDateKey, type GlobalFilters } from '@/lib/filters'

import { buildAlerts, DEMO_CONNECTOR_FAILURE } from './generators/alerts'
import { demoSignups } from './generators/signupsReport'
import { buildDigest } from './generators/digest'
import { DemoDataset, round2, scopeFor } from './generators/engine'
import { demoLeakage, demoLifecycle, demoOrders, demoOrdersCsv } from './generators/revenueLeakage'
import {
  buildChannels,
  buildFunnel,
  buildKpis,
  buildRevenueTrend,
  buildTopCompetitions,
  periodInfo,
  type Built,
  type DemoChannelPerformance,
  type DemoCompetitionPerformance,
  type DemoKpi,
} from './generators/overview'
import { AutomationStore } from './automation/store'
import { parseCompetitorCsv, parseTrackedCompetitionCsv } from './market/csv'
import { reportRowsFor } from './reports/engine'
import { ReportsStore } from './reports/store'
import {
  buildActivitySummary,
  buildCompetitors,
  buildObservations,
  buildPricingSummary,
  buildPrizeTrends,
  buildTrackedCompetitions,
  inWindow,
  nextId,
  seedState,
  type MarketState,
} from './market/engine'
import { marketFixtureFor } from './market/fixtures'
import { ListingsStore } from './listings/store'
import { CampaignsStore } from './campaigns/store'
import { buildDigestSections } from './insights/digestSections'
import { buildDigestFacts, DEMO_REGENERATIONS_PER_DAY, demoDigestReport } from './insights/digestReport'
import { buildRecommendations } from './insights/recommendations'
import { buildTrends } from './insights/trends'
import { DEMO_TENANTS, type TenantFixture } from './tenants'
import { overviewMeta } from '@/api/overview'
import {
  revenueBreakdownFromWire,
  revenueSeriesFromWire,
  revenueSummaryFromWire,
  revenueTimingFromWire,
  type RevenueBreakdown,
  type RevenueBreakdownQuery,
  type RevenueMetaOut,
  type RevenueQuery,
  type RevenueSeries,
  type RevenueSeriesQuery,
  type RevenueSummary,
  type RevenueTiming,
} from '@/api/revenue'
import { MockRevenue } from './generators/revenue'
import { MockTrackingBaselineStore } from './dataQuality'

export interface MockApiClientOptions {
  tenants?: readonly TenantFixture[]
  /** The clock the demo trades against; fix it for stable tests. */
  now?: () => Date
  /** Defaults to every permission, as an Owner. */
  permissions?: readonly Permission[]
  role?: string
  /** Defaults to every module enabled. */
  entitlements?: Partial<Record<ModuleKey, boolean>>
  /** Artificial latency, so loading states are visible in development. */
  latencyMs?: number
  /**
   * Start with no session, so the signed-out page can be developed and tested
   * without the API. `VITE_MOCK_SIGNED_OUT=1` turns it on for `npm run dev`.
   */
  signedOut?: boolean
  /**
   * Present the session as a TmT support impersonation, so the banner can be
   * developed and tested without the internal API. Defaults to none.
   */
  impersonation?: Impersonation | null
  /**
   * The tracking baseline the demo tenant starts with (`YYYY-MM-DD`), or null for none.
   * Defaults to the tenant fixture's `trackingBaselineDaysAgo`.
   */
  trackingBaseline?: string | null
}

/**
 * The TmT-owned hostname in the demo, and therefore the CNAME target Settings >
 * Domains tells the customer to point at. It mirrors
 * `settings.platform_host`; the demo has no server to ask for it.
 */
const DEMO_PLATFORM_HOST = 'app.insights.tmtsoftware.co.uk'

/**
 * The demo backend. Every response is generated from a tenant fixture and
 * marked `source: 'demo'`, so the shell badges it as demo data wherever it
 * appears.
 */
export class MockApiClient implements ApiClient {
  readonly kind = 'mock' as const
  private readonly tenantFixtures: readonly TenantFixture[]
  private readonly clock: () => Date
  private activeTenantId: string
  /** Market Intelligence's per-tenant state, seeded on first access and mutated by manual entry / CSV import. */
  private readonly marketStates = new Map<string, MarketState>()
  private readonly listingsStores = new Map<string, ListingsStore>()
  private readonly campaignsStores = new Map<string, CampaignsStore>()
  private readonly automationStores = new Map<string, AutomationStore>()
  private readonly reportsStores = new Map<string, ReportsStore>()
  /** Acknowledge/dismiss on an alert or recommendation persists here for this client's lifetime — "the session" (pivot doc §3a). */
  private readonly alertStates = new Map<string, 'acknowledged' | 'dismissed'>()
  private readonly dismissedRecommendations = new Set<string>()
  /** Settings > Users, in memory: members and invitations that survive this client. */
  readonly users: UsersBackend
  /** Settings > Domains, in memory: one custom domain and its onboarding state. */
  readonly domains: DomainsBackend
  /** Settings > Websites, in memory: each website's site credentials. */
  readonly siteCredentials: SiteCredentialsBackend
  private signedOut: boolean
  /** Settings › Tracking, in memory: each tenant's tracking baseline. */
  private readonly baselines: MockTrackingBaselineStore

  constructor(private readonly options: MockApiClientOptions = {}) {
    this.tenantFixtures = options.tenants ?? DEMO_TENANTS
    if (this.tenantFixtures.length === 0) throw new Error('MockApiClient needs at least one tenant')
    this.clock = options.now ?? (() => new Date())
    this.activeTenantId = (this.tenantFixtures[0] as TenantFixture).id
    this.signedOut = options.signedOut ?? import.meta.env.VITE_MOCK_SIGNED_OUT === '1'
    const { user } = this.tenant
    this.users = new MockUsersBackend({
      now: this.clock,
      self: { email: user.email, name: user.name, role: options.role ?? user.role },
    })
    this.domains = new MockDomainsBackend({ now: this.clock, platformHost: DEMO_PLATFORM_HOST })
    const siteCredentials = new MockSiteCredentialsBackend({
      now: this.clock,
      websites: this.tenant.websites.map((site) => ({ id: site.id, environment: site.environment })),
    })
    seedDemoSiteCredentials(siteCredentials, this.tenant.websites, this.clock())
    this.siteCredentials = siteCredentials
    this.baselines = new MockTrackingBaselineStore(this.clock)
  }

  private get tenant(): TenantFixture {
    return (
      this.tenantFixtures.find((tenant) => tenant.id === this.activeTenantId) ??
      (this.tenantFixtures[0] as TenantFixture)
    )
  }

  /** The active tenant's baseline: the options' seed, else the fixture's, else none. */
  private baseline(): TrackingBaseline {
    const { trackingBaselineDaysAgo: daysAgo } = this.tenant
    const fixtureSeed = daysAgo === undefined ? null : addDays(toDateKey(this.clock()), -daysAgo)
    const seed = this.options.trackingBaseline !== undefined ? this.options.trackingBaseline : fixtureSeed
    return this.baselines.get(this.tenant.id, seed)
  }

  private get trackingReliableFrom(): string | null {
    return this.baseline().trackingReliableFrom
  }

  private dataset(): DemoDataset {
    return new DemoDataset(this.tenant, this.clock())
  }

  private marketState(): MarketState {
    const tenantId = this.tenant.id
    let state = this.marketStates.get(tenantId)
    if (!state) {
      state = seedState(marketFixtureFor(tenantId), toDateKey(this.clock()))
      this.marketStates.set(tenantId, state)
    }
    return state
  }

  /** One listings store per tenant, so a session's edits survive navigation and tenant switches keep their own state. */
  private listingsStore(): ListingsStore {
    const { tenant } = this
    let store = this.listingsStores.get(tenant.id)
    if (!store) {
      store = new ListingsStore(tenant, this.clock())
      this.listingsStores.set(tenant.id, store)
    }
    return store
  }

  private respond<T>(value: T): Promise<T> {
    const latency = this.options.latencyMs ?? 0
    if (latency <= 0) return Promise.resolve(value)
    return new Promise((resolve) => setTimeout(() => resolve(value), latency))
  }

  private meta(period?: PeriodInfo): DataMeta {
    return {
      source: 'demo',
      lastUpdatedAt: new Date(this.clock().getTime() - 4 * 60_000).toISOString(),
      ...(period ? { period } : {}),
    }
  }

  private envelope<T>(built: Built<T>): Promise<Envelope<T>> {
    return this.respond({ meta: this.meta(built.period), data: built.data })
  }

  private summary(tenant: TenantFixture): TenantSummary {
    return {
      id: tenant.id,
      name: tenant.name,
      logoUrl: null,
      accentColor: tenant.accentColor,
      currency: tenant.currency,
      locale: tenant.locale,
      current: tenant.id === this.activeTenantId,
    }
  }

  health(): Promise<HealthStatus> {
    return this.respond({ status: 'ok' })
  }

  currentTenant(): Promise<TenantSummary> {
    return this.respond(this.summary(this.tenant))
  }

  tenants(): Promise<TenantSummary[]> {
    return this.respond(this.tenantFixtures.map((tenant) => this.summary(tenant)))
  }

  switchTenant = async (tenantId: string): Promise<void> => {
    if (!this.tenantFixtures.some((tenant) => tenant.id === tenantId)) {
      throw new Error(`unknown demo tenant: ${tenantId}`)
    }
    this.activeTenantId = tenantId
    await this.respond(undefined)
  }

  session(): Promise<SessionUser> {
    // The same refusal the API gives a browser with no session cookie, so the
    // signed-out gate is exercised against the demo client exactly as it is
    // against the real one.
    if (this.signedOut) {
      return Promise.reject(new ApiError(401, 'authentication_error', 'not signed in'))
    }
    const role = this.options.role ?? this.tenant.user.role
    return this.respond({
      userId: 'usr_self',
      name: this.tenant.user.name,
      email: this.tenant.user.email,
      role,
      roleLabel: ROLE_LABELS[role] ?? role,
      permissions: [...(this.options.permissions ?? PERMISSIONS)],
      impersonation: this.options.impersonation ?? null,
    })
  }

  /** The demo has no identity provider, so there is nowhere further to send the browser. */
  async signOut(): Promise<string | null> {
    this.signedOut = true
    return this.respond(null)
  }

  entitlements(): Promise<Entitlements> {
    const modules = Object.fromEntries(
      MODULE_KEYS.map((key) => [key, this.options.entitlements?.[key] ?? true]),
    ) as Record<ModuleKey, boolean>
    return this.respond({ modules })
  }

  environmentSelector(environments: readonly Environment[]): Promise<EnvironmentSelector> {
    const active: Environment[] = environments.length > 0 ? [...environments] : ['production']
    return this.respond({
      default: ['production'],
      active,
      non_production_active: isNonProduction(environments),
      options: (Object.keys(ENVIRONMENT_LABELS) as Environment[]).map((value) => ({
        value,
        label: ENVIRONMENT_LABELS[value],
        is_production: value === 'production',
        website_count: this.tenant.websites.filter((website) => website.environment === value).length,
      })),
    })
  }

  filterOptions(): Promise<FilterOptions> {
    const { tenant } = this
    const dataset = this.dataset()
    return this.respond({
      website: tenant.websites.map((website) => ({ value: website.id, label: website.name })),
      competition: tenant.competitions
        .map((competition) => {
          const { opens, closes } = dataset.schedule(competition)
          const group = closes < dataset.today ? 'Closed' : opens > dataset.today ? 'Upcoming' : 'Open'
          return { value: competition.id, label: competition.name, group }
        })
        .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label)),
      channel: tenant.channels.map((channel) => ({
        value: channel.id,
        label: channel.name,
        group: channel.kind === 'offline' ? 'Offline' : 'Digital',
      })),
      campaign: tenant.campaigns.map((campaign) => ({ value: campaign.id, label: campaign.name })),
      creative: tenant.creatives.map((creative) => ({ value: creative.id, label: creative.name })),
      device: tenant.devices.map((device) => ({ value: device.id, label: device.label })),
      customer_type: tenant.customerTypes.map((type) => ({ value: type.id, label: type.label })),
    })
  }

  freshness(): Promise<DataMeta> {
    return this.respond(this.meta())
  }

  dailyMetrics(environments: readonly Environment[]): Promise<DailyMetrics> {
    const dataset = this.dataset()
    const selected: Environment[] = environments.length > 0 ? [...environments] : ['production']
    const end = dataset.today
    const window = { from: addDays(end, -30), to: end, days: 31 }
    const rows = this.tenant.websites
      .filter((website) => selected.includes(website.environment))
      .flatMap((website) => {
        const scope = {
          ...scopeFor(this.tenant, DEFAULT_FILTERS),
          traffic: website.trafficShare,
          includesProduction: website.environment === 'production',
        }
        return eachDay(window).map((date) => {
          const day = dataset.day(date, scope)
          const sessions = Math.round(day.stages.visitors * 1.25)
          return {
            website_id: website.id,
            environment: website.environment,
            metric_date: date,
            sessions,
            events: sessions * 7,
            orders: day.orders,
            gross_revenue: round2(day.revenue),
            currency: this.tenant.currency,
          }
        })
      })
    return this.respond({
      environments: selected,
      non_production: isNonProduction(environments),
      start: window.from,
      end: window.to,
      rows,
    })
  }

  overviewKpis(filters: GlobalFilters): Promise<Envelope<DemoKpi[]>> {
    return this.envelope(buildKpis(this.dataset(), filters))
  }

  revenueTrend(filters: GlobalFilters, window: TrendWindow): Promise<Envelope<RevenueTrend>> {
    return this.envelope(buildRevenueTrend(this.dataset(), filters, window))
  }

  funnel(filters: GlobalFilters): Promise<Envelope<FunnelStage[]>> {
    return this.envelope(buildFunnel(this.dataset(), filters))
  }

  topCompetitions(filters: GlobalFilters, limit: number): Promise<Envelope<DemoCompetitionPerformance[]>> {
    return this.envelope(buildTopCompetitions(this.dataset(), filters, limit))
  }

  channelPerformance(filters: GlobalFilters): Promise<Envelope<DemoChannelPerformance[]>> {
    return this.envelope(buildChannels(this.dataset(), filters))
  }

  /** Scopes an id to the active tenant, so switching demo tenants cannot leak one tenant's acknowledged alerts onto another's. */
  private sessionKey(id: string): string {
    return `${this.activeTenantId}:${id}`
  }

  /**
   * Alerts for `dataset`, with acknowledge/dismiss state applied and dismissed
   * ones dropped. The one place that does this filtering — `digest()` reuses
   * it too, so the AI Digest can never show an alert a session already
   * cleared elsewhere.
   */
  private alertsFor(dataset: DemoDataset, filters: GlobalFilters): Alert[] {
    return buildAlerts(dataset, filters)
      .map((alert) => ({
        ...alert,
        state: (this.alertStates.get(this.sessionKey(alert.id)) ?? 'new') as AlertState,
      }))
      .filter((alert) => alert.state !== 'dismissed')
  }

  /** Recommendations for `dataset` with dismissed ones dropped — see `alertsFor`. */
  private recommendationsFor(dataset: DemoDataset, filters: GlobalFilters): Recommendation[] {
    return buildRecommendations(dataset, filters).filter(
      (recommendation) => !this.dismissedRecommendations.has(this.sessionKey(recommendation.id)),
    )
  }

  /** As the API: production, as things stand now, whatever the page's filters. */
  activeAlerts(): Promise<Envelope<Alert[]>> {
    return this.envelope({ data: this.alertsFor(this.dataset(), DEFAULT_FILTERS) })
  }

  async acknowledgeAlert(id: string): Promise<void> {
    this.alertStates.set(this.sessionKey(id), 'acknowledged')
    await this.respond(undefined)
  }

  async dismissAlert(id: string): Promise<void> {
    this.alertStates.set(this.sessionKey(id), 'dismissed')
    await this.respond(undefined)
  }

  /** The digest's own cadence over the demo's defaults: a digest is tenant-wide and takes no global filter. */
  private digestFilters(period: DigestPeriod): GlobalFilters {
    return { ...DEFAULT_FILTERS, range: period === 'daily' ? 'today' : '7d' }
  }

  /** The versions written so far of each period's demo digest, oldest first as the API keeps them, per tenant. */
  private readonly digestVersions = new Map<string, DigestVersion[]>()
  /** Regenerations asked, per tenant and UTC day: the API's limit counts daily and weekly together. */
  private readonly regenerations = new Map<string, number>()

  /** Version 1 is written on first read, as the scheduled job would have. */
  private versionsFor(period: DigestPeriod, generatedAt: string): DigestVersion[] {
    const key = this.sessionKey(`digest:${period}`)
    let versions = this.digestVersions.get(key)
    if (!versions) {
      versions = [{ version: 1, generatedAt, writer: 'template' }]
      this.digestVersions.set(key, versions)
    }
    return versions
  }

  private digestReport(period: DigestPeriod, version?: number): Envelope<DigestReport> {
    const dataset = this.dataset()
    const effective = this.digestFilters(period)
    const base = buildDigest(dataset, effective)
    const alerts = this.alertsFor(dataset, effective)
    const recommendations = this.recommendationsFor(dataset, effective)
    const sections = buildDigestSections(dataset, effective, alerts, recommendations)
    const from = base.period?.from ?? toDateKey(dataset.now)
    const to = base.period?.to ?? from
    const versions = this.versionsFor(period, base.data.generatedAt)
    if (version !== undefined && !versions.some((item) => item.version === version)) {
      throw new ApiError(404, 'not_found', 'This period has no such version.')
    }
    const facts = buildDigestFacts(dataset, effective, from, to)
    const data = demoDigestReport(period, from, to, { ...base.data, ...sections }, facts, versions, version)
    return { meta: this.meta(base.period), data }
  }

  async digest(query: DigestQuery): Promise<Envelope<DigestReport>> {
    return this.respond(this.digestReport(query.period, query.version))
  }

  /** A new template version, up to the API's daily limit; beyond it the same 429 the API answers. */
  async regenerateDigest(period: DigestPeriod): Promise<Envelope<DigestReport>> {
    const now = this.clock()
    const day = this.sessionKey(`regenerate:${now.toISOString().slice(0, 10)}`)
    const used = this.regenerations.get(day) ?? 0
    if (used >= DEMO_REGENERATIONS_PER_DAY) {
      const midnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
      throw new ApiError(429, 'rate_limited', 'This digest has been regenerated as often as it can be today.', undefined, {
        retry_after_seconds: Math.ceil((midnight - now.getTime()) / 1000),
        limit: DEMO_REGENERATIONS_PER_DAY,
      })
    }
    this.regenerations.set(day, used + 1)
    const versions = this.versionsFor(period, now.toISOString())
    const next = Math.max(0, ...versions.map((item) => item.version)) + 1
    versions.push({ version: next, generatedAt: now.toISOString(), writer: 'template' })
    return this.respond(this.digestReport(period))
  }

  recommendations(filters: GlobalFilters): Promise<Envelope<Recommendation[]>> {
    return this.envelope({ data: this.recommendationsFor(this.dataset(), filters) })
  }

  async dismissRecommendation(id: string): Promise<void> {
    this.dismissedRecommendations.add(this.sessionKey(id))
    await this.respond(undefined)
  }

  trends(): Promise<Envelope<Trend[]>> {
    return this.envelope({ data: buildTrends(this.dataset(), DEFAULT_FILTERS) })
  }

  automations(): Promise<Envelope<AutomationSummary[]>> {
    return this.envelope({
      data: this.tenant.automations.map((automation) => ({ ...automation })),
    })
  }

  // --- market intelligence -----------------------------------------------

  marketCompetitors(): Promise<Envelope<MarketCompetitor[]>> {
    const today = toDateKey(this.clock())
    return this.respond({ meta: this.meta(), data: buildCompetitors(this.marketState(), today) })
  }

  marketTrackedCompetitions(): Promise<Envelope<TrackedCompetition[]>> {
    const today = toDateKey(this.clock())
    return this.respond({ meta: this.meta(), data: buildTrackedCompetitions(this.marketState(), today) })
  }

  marketObservations(filters: GlobalFilters): Promise<Envelope<MarketObservation[]>> {
    const now = this.clock()
    const period = resolvePeriod(filters, now)
    const windowed = inWindow(buildObservations(this.marketState()), period.current)
    return this.respond({
      meta: this.meta({
        from: period.current.from,
        to: period.current.to,
        label: period.label,
        compareLabel: period.compareLabel,
        previousFrom: period.previous?.from ?? null,
        previousTo: period.previous?.to ?? null,
      }),
      data: windowed,
    })
  }

  marketActivitySummary(filters: GlobalFilters): Promise<Envelope<MarketActivitySummary>> {
    const now = this.clock()
    const today = toDateKey(now)
    const period = resolvePeriod(filters, now)
    const windowed = inWindow(buildObservations(this.marketState()), period.current)
    const tracked = buildTrackedCompetitions(this.marketState(), today)
    return this.respond({ meta: this.meta(), data: buildActivitySummary(windowed, tracked) })
  }

  marketPricingSummary(): Promise<Envelope<MarketPricingSummary>> {
    const today = toDateKey(this.clock())
    const tracked = buildTrackedCompetitions(this.marketState(), today)
    return this.respond({ meta: this.meta(), data: buildPricingSummary(tracked) })
  }

  marketPrizeTrends(filters: GlobalFilters): Promise<Envelope<PrizeTrendPoint[]>> {
    const now = this.clock()
    const today = toDateKey(now)
    const period = resolvePeriod(filters, now)
    const tracked = buildTrackedCompetitions(this.marketState(), today)
    return this.respond({
      meta: this.meta({
        from: period.current.from,
        to: period.current.to,
        label: period.label,
        compareLabel: period.compareLabel,
        previousFrom: period.previous?.from ?? null,
        previousTo: period.previous?.to ?? null,
      }),
      data: buildPrizeTrends(tracked, period.current, period.previous),
    })
  }

  marketMonitoringStatus(): Promise<Envelope<MonitoringStatus>> {
    return this.envelope({
      data: {
        enabled: false,
        note: 'Automated competitor monitoring is not enabled. Enabling it needs a compliance and legal review before any external site is queried — this release supports manual entry and CSV import only.',
      },
    })
  }

  addMarketCompetitor(input: CompetitorInput): Promise<MarketCompetitor> {
    const state = this.marketState()
    const today = toDateKey(this.clock())
    const id = nextId('cmp_mkt')
    state.competitors.push({
      id,
      name: input.name,
      domain: input.domain,
      brand: input.brand,
      frequency: input.frequency,
      typicalTicketPrice: input.typicalTicketPrice,
      prizeCategories: input.prizeCategories,
      trackedSince: today,
    })
    state.observations.push({
      id: nextId('mkt_obs'),
      competitorId: id,
      trackedCompetitionId: null,
      activityType: 'launch',
      fact: `${input.name} added to tracked competitors.`,
      occurredAt: today,
      source: 'manual',
      confidence: 'high',
    })
    const competitor = buildCompetitors(state, today).find((candidate) => candidate.id === id)
    return this.respond(competitor as MarketCompetitor)
  }

  importMarketCompetitorsCsv(csv: string): Promise<CsvImportResult> {
    const state = this.marketState()
    const today = toDateKey(this.clock())
    const { rows, errors } = parseCompetitorCsv(csv)
    for (const row of rows) {
      state.competitors.push({ id: nextId('cmp_mkt'), ...row, trackedSince: today })
    }
    if (rows.length > 0) {
      state.observations.push({
        id: nextId('mkt_obs'),
        competitorId: null,
        trackedCompetitionId: null,
        activityType: 'launch',
        fact: `Imported ${rows.length} competitor${rows.length === 1 ? '' : 's'} via CSV.`,
        occurredAt: today,
        source: 'import',
        confidence: 'medium',
      })
    }
    return this.respond({ added: rows.length, errors })
  }

  addTrackedCompetition(input: TrackedCompetitionInput): Promise<TrackedCompetition> {
    const state = this.marketState()
    const today = toDateKey(this.clock())
    const id = nextId('tc_mkt')
    state.trackedCompetitions.push({
      id,
      competitorId: input.competitorId,
      prize: input.prize,
      category: input.category,
      ticketPrice: input.ticketPrice,
      maxEntries: input.maxEntries,
      instantWins: input.instantWins,
      startDate: input.startDate,
      endDate: input.endDate,
      promotionalMessaging: input.promotionalMessaging,
    })
    const competitor = state.competitors.find((candidate) => candidate.id === input.competitorId)
    state.observations.push({
      id: nextId('mkt_obs'),
      competitorId: input.competitorId,
      trackedCompetitionId: id,
      activityType: 'launch',
      fact: `${competitor?.name ?? 'A tracked competitor'} — ${input.prize} added.`,
      occurredAt: today,
      source: 'manual',
      confidence: 'high',
    })
    const tracked = buildTrackedCompetitions(state, today).find((candidate) => candidate.id === id)
    return this.respond(tracked as TrackedCompetition)
  }

  importTrackedCompetitionsCsv(csv: string): Promise<CsvImportResult> {
    const state = this.marketState()
    const today = toDateKey(this.clock())
    const { rows, errors } = parseTrackedCompetitionCsv(csv, (name) => {
      const match = state.competitors.find((candidate) => candidate.name.toLowerCase() === name.trim().toLowerCase())
      return match?.id
    })
    for (const row of rows) {
      state.trackedCompetitions.push({ id: nextId('tc_mkt'), ...row })
    }
    if (rows.length > 0) {
      state.observations.push({
        id: nextId('mkt_obs'),
        competitorId: null,
        trackedCompetitionId: null,
        activityType: 'launch',
        fact: `Imported ${rows.length} tracked competition${rows.length === 1 ? '' : 's'} via CSV.`,
        occurredAt: today,
        source: 'import',
        confidence: 'medium',
      })
    }
    return this.respond({ added: rows.length, errors })
  }

  // --- competition management: listings -------------------------------------

  listings(): Promise<Envelope<Listing[]>> {
    return this.envelope({ data: this.listingsStore().list(this.dataset()) })
  }

  listing(id: string): Promise<Envelope<Listing | null>> {
    return this.envelope({ data: this.listingsStore().get(id, this.dataset()) })
  }

  createListing(input: ListingInput): Promise<Envelope<Listing>> {
    return this.envelope({ data: this.listingsStore().create(input, this.clock()) })
  }

  // async: the store can throw synchronously (missing id, publishing before approval), and this
  // keeps that failure a promise rejection, consistent with every method's declared return type.
  async updateListing(id: string, input: ListingInput): Promise<Envelope<Listing>> {
    return this.envelope({ data: this.listingsStore().update(id, input, this.clock()) })
  }

  async generateListingContent(id: string): Promise<Envelope<Listing>> {
    return this.envelope({ data: this.listingsStore().generateContent(id, this.clock()) })
  }

  async setListingApproval(id: string, group: FieldGroupKey, approved: boolean): Promise<Envelope<Listing>> {
    return this.envelope({ data: this.listingsStore().setApproval(id, group, approved, this.clock()) })
  }

  async publishListing(id: string, target: PublishTargetKey): Promise<Envelope<Listing>> {
    return this.envelope({ data: this.listingsStore().publish(id, target, this.clock()) })
  }

  listingTemplates(): Promise<Envelope<ListingTemplate[]>> {
    return this.envelope({ data: this.listingsStore().listTemplates() })
  }

  saveListingTemplate(input: ListingTemplateInput): Promise<Envelope<ListingTemplate>> {
    return this.envelope({ data: this.listingsStore().saveTemplate(input) })
  }

  // --- marketing: campaigns (pivot doc §6 Stage 2) ---------------------------

  /** One campaigns store per tenant, seeded on first use and kept for the session, like listings. */
  private campaignsStore(): CampaignsStore {
    const { tenant } = this
    let store = this.campaignsStores.get(tenant.id)
    if (!store) {
      store = new CampaignsStore(tenant, this.clock())
      this.campaignsStores.set(tenant.id, store)
    }
    return store
  }

  campaignWebsites(): Promise<CampaignWebsite[]> {
    return this.respond(this.campaignsStore().websites())
  }

  campaigns(filters: GlobalFilters): Promise<Envelope<CampaignListItem[]>> {
    const dataset = this.dataset()
    return this.envelope({
      data: this.campaignsStore().list(dataset, filters),
      period: periodInfo(resolvePeriod(filters, dataset.now)),
    })
  }

  campaign(id: string): Promise<Envelope<CampaignDetail | null>> {
    return this.envelope({ data: this.campaignsStore().detail(id) })
  }

  // async from here on: the store throws its refusals synchronously, and they must reach the caller as rejections.
  async campaignPerformance(id: string, filters: GlobalFilters): Promise<Envelope<CampaignPerformance>> {
    const dataset = this.dataset()
    return this.envelope({
      data: this.campaignsStore().performance(id, dataset, filters),
      period: periodInfo(resolvePeriod(filters, dataset.now)),
    })
  }

  async createCampaign(draft: CampaignDraft, intent: SaveIntent): Promise<Envelope<CampaignDetail>> {
    return this.envelope({ data: this.campaignsStore().create(draft, intent, this.clock()) })
  }

  async updateCampaign(id: string, patch: CampaignPatch): Promise<Envelope<CampaignDetail>> {
    return this.envelope({ data: this.campaignsStore().update(id, patch, this.clock()) })
  }

  async updateCampaignChannel(campaignId: string, channelId: string, patch: ChannelPatch): Promise<Envelope<CampaignDetail>> {
    return this.envelope({ data: this.campaignsStore().updateChannel(campaignId, channelId, patch, this.clock()) })
  }

  async updateCampaignCreative(
    campaignId: string,
    creative: Pick<CampaignCreative, 'id' | 'channelId'>,
    patch: CreativePatch,
  ): Promise<Envelope<CampaignDetail>> {
    return this.envelope({ data: this.campaignsStore().updateCreative(campaignId, creative.id, patch, this.clock()) })
  }

  async generateCampaignTracking(campaignId: string, input: TrackingGenerateInput): Promise<Envelope<CampaignDetail>> {
    return this.envelope({ data: this.campaignsStore().generateTracking(campaignId, input, this.clock()) })
  }

  async updateCampaignTracking(campaignId: string, trackingId: string, patch: TrackingPatch): Promise<Envelope<CampaignDetail>> {
    return this.envelope({ data: this.campaignsStore().updateTracking(campaignId, trackingId, patch, this.clock()) })
  }

  /** Drawn locally: a demo QR code never reaches any service. */
  async campaignQrSvg(campaignId: string, trackingId: string): Promise<string> {
    return this.respond(this.campaignsStore().qrSvg(campaignId, trackingId))
  }

  // --- automation (pivot doc §6 Stage 4) -------------------------------------

  /** One automation store per tenant, seeded on first use and kept for the session, like listings and campaigns. */
  private automationStore(): AutomationStore {
    const { tenant } = this
    let store = this.automationStores.get(tenant.id)
    if (!store) {
      store = new AutomationStore(tenant)
      this.automationStores.set(tenant.id, store)
    }
    return store
  }

  triggerDefinitions(): Promise<TriggerDefinition[]> {
    return this.respond([...TRIGGER_DEFINITION_LIST])
  }

  actionDefinitions(): Promise<ActionDefinition[]> {
    return this.respond([...ACTION_DEFINITION_LIST])
  }

  workflows(): Promise<Envelope<Workflow[]>> {
    return this.envelope({ data: this.automationStore().workflows(toDateKey(this.clock())) })
  }

  workflow(id: string): Promise<Envelope<Workflow | null>> {
    return this.envelope({ data: this.automationStore().workflow(id, toDateKey(this.clock())) })
  }

  async setWorkflowStatus(id: string, status: AutomationStatus): Promise<Envelope<Workflow>> {
    return this.envelope({ data: this.automationStore().setStatus(id, status, toDateKey(this.clock())) })
  }

  scheduledActions(): Promise<Envelope<ScheduledAction[]>> {
    return this.envelope({ data: this.automationStore().scheduledActions(toDateKey(this.clock())) })
  }

  async cancelScheduledAction(id: string): Promise<Envelope<ScheduledAction[]>> {
    return this.envelope({ data: this.automationStore().cancelScheduledAction(id, toDateKey(this.clock())) })
  }

  automationActivity(): Promise<Envelope<ActivityEntry[]>> {
    return this.envelope({ data: this.automationStore().activity(toDateKey(this.clock())) })
  }

  // --- reports (pivot doc §6 Stage 4) ----------------------------------------

  /** One reports store per tenant, seeded on first use and kept for the session. */
  private reportsStore(): ReportsStore {
    const { tenant } = this
    let store = this.reportsStores.get(tenant.id)
    if (!store) {
      store = new ReportsStore(tenant, toDateKey(this.clock()))
      this.reportsStores.set(tenant.id, store)
    }
    return store
  }

  savedReports(): Promise<Envelope<SavedReport[]>> {
    return this.envelope({ data: this.reportsStore().list() })
  }

  savedReport(id: string): Promise<Envelope<SavedReport | null>> {
    return this.envelope({ data: this.reportsStore().get(id) })
  }

  async createSavedReport(input: ReportInput): Promise<Envelope<SavedReport>> {
    return this.envelope({ data: this.reportsStore().create(input, toDateKey(this.clock())) })
  }

  async duplicateSavedReport(id: string): Promise<Envelope<SavedReport>> {
    return this.envelope({ data: this.reportsStore().duplicate(id, toDateKey(this.clock())) })
  }

  async scheduleSavedReport(id: string, schedule: ReportSchedule | null): Promise<Envelope<SavedReport>> {
    return this.envelope({ data: this.reportsStore().schedule(id, schedule, toDateKey(this.clock())) })
  }

  reportRows(dataset: ReportDataset, filters: GlobalFilters): Promise<Envelope<ReportRow[]>> {
    const today = toDateKey(this.clock())
    const workflows = this.automationStore().workflows(today)
    return this.envelope({ data: reportRowsFor(dataset, this.dataset(), workflows, filters, today) })
  }

  exports(): Promise<Envelope<ReportExport[]>> {
    return this.envelope({ data: this.reportsStore().exports() })
  }

  async requestExport(input: RequestExportInput): Promise<Envelope<ReportExport>> {
    return this.envelope({
      data: this.reportsStore().requestExport(input.reportId, input.dataset, input.format, toDateKey(this.clock())),
    })
  }

  // --- integrations: Meta (wave 7b) --------------------------------------------

  /** This tenant's demo Meta connection; public so a test can make it need reconnecting. */
  get metaConnection(): MockMetaStore {
    return metaStoreFor(this, this.tenant, () => this.campaignsStore().websites(), this.clock)
  }

  metaIntegration(): Promise<MetaIntegrationState> {
    return this.respond(this.metaConnection.state())
  }

  async connectMeta(): Promise<MetaConnectStart> {
    return this.respond(this.metaConnection.connect())
  }

  async disconnectMeta(): Promise<MetaIntegrationState> {
    return this.respond(this.metaConnection.disconnect())
  }

  async runMetaImportNow(): Promise<MetaIntegrationState> {
    return this.respond(this.metaConnection.runImportNow())
  }

  async metaAdAccounts(): Promise<MetaAdAccountList> {
    return this.respond(this.metaConnection.adAccounts())
  }

  async saveMetaAdAccounts(mappings: readonly MetaAdAccountMapping[]): Promise<MetaMappingResult> {
    return this.respond(this.metaConnection.save(mappings))
  }

  // --- Google Analytics 4 (wave 7b) ---------------------------------------------

  /** This tenant's demo GA4 connection and traffic. */
  get ga4Connection(): MockGa4Store {
    return ga4StoreFor(this, this.tenant.id, () => this.campaignsStore().websites(), this.clock)
  }

  ga4Integration(): Promise<Ga4Integration> {
    return this.respond(this.ga4Connection.overview())
  }

  async connectGa4(input: { propertyId: string; websiteId?: string }): Promise<Ga4Property> {
    return this.respond(this.ga4Connection.connect(input.propertyId, input.websiteId))
  }

  async disconnectGa4(ga4PropertyId: string): Promise<Ga4Property> {
    return this.respond(this.ga4Connection.disconnect(ga4PropertyId))
  }

  async runGa4Import(ga4PropertyId: string): Promise<Ga4Property[]>
  async runGa4Import(): Promise<Ga4ImportRun>
  async runGa4Import(ga4PropertyId?: string): Promise<Ga4Property[] | Ga4ImportRun> {
    return this.respond(ga4PropertyId ? this.ga4Connection.runNow(ga4PropertyId) : this.ga4Connection.runNow())
  }

  async checkGa4Streams(propertyId: string): Promise<Ga4StreamCheck> {
    return this.respond(this.ga4Connection.checkStreams(propertyId))
  }

  async traffic(query: Ga4TrafficQuery): Promise<Ga4TrafficReport> {
    return this.respond(this.ga4Connection.traffic(query))
  }

  async trafficCsv(query: Ga4TrafficQuery): Promise<Blob> {
    return this.respond(this.ga4Connection.trafficCsv(query))
  }

  // --- Data quality: the tracking baseline (wave 20) ------------------------------

  async trackingBaseline(): Promise<TrackingBaseline> {
    return this.respond(this.baseline())
  }

  /** Refused as the API refuses it: without `settings.manage`, or for a date after today. */
  async setTrackingBaseline(input: TrackingBaselineInput): Promise<TrackingBaseline> {
    const permissions = this.options.permissions ?? PERMISSIONS
    const allowed = permissions.includes('settings.manage')
    return this.respond(this.baselines.set(this.tenant.id, input, 'usr_self', allowed))
  }

  // --- First-party traffic: the Traffic page's TmT source (wave 8, card 3d9c00e6) ---

  async firstPartyTraffic(query: FirstPartyTrafficQuery): Promise<FirstPartyTrafficReport> {
    return this.respond(demoFirstPartyTraffic(query, this.tenant.currency, this.clock(), this.trackingReliableFrom))
  }

  async firstPartyTrafficCsv(query: FirstPartyTrafficQuery): Promise<Blob> {
    return this.respond(demoFirstPartyTrafficCsv(query, this.tenant.currency, this.clock(), this.trackingReliableFrom))
  }

  // --- First-party attribution: Marketing › Attribution (card 3d9c00e6) ---------

  /** Meta ids resolve only while the demo Meta connection feeds a website, as Integrations shows it. */
  async attributionConversions(query: AttributionQuery): Promise<AttributionReport> {
    const meta = this.metaConnection.state()
    const metaActive = meta.status !== 'not_connected' && meta.adAccounts.length > 0
    return this.respond(demoAttribution(query, this.tenant.currency, metaActive, this.trackingReliableFrom))
  }

  // --- Meta advertising campaign report (wave 7b) -------------------------------

  /** Reads the same demo Meta connection as Integrations, so the two can never disagree. */
  private get advertisingStore(): MockAdvertisingStore {
    return advertisingStoreFor(this, this.tenant.id, () => this.metaConnection.state(), () => this.tenant.currency)
  }

  async advertisingCampaigns(query: AdvertisingQuery): Promise<AdvertisingReport> {
    return this.respond(this.advertisingStore.report(query))
  }

  async advertisingCampaignsCsv(query: AdvertisingQuery): Promise<Blob> {
    return this.respond(this.advertisingStore.csv(query))
  }

  // --- Marketing › Advertising: the overview and the reconciliation (wave 16) ------

  /** Reads the same demo Meta connection as Integrations and the campaign report. */
  private get advertisingOverviewStore(): MockAdvertisingOverviewStore {
    return advertisingOverviewStoreFor(
      this,
      this.tenant.id,
      () => this.metaConnection.state(),
      () => this.tenant.currency,
      this.clock,
      () => this.trackingReliableFrom,
    )
  }

  async advertisingOverview(query: AdOverviewQuery): Promise<AdvertisingOverview> {
    return this.respond(this.advertisingOverviewStore.overview(query))
  }

  async advertisingOverviewTrend(query: AdOverviewTrendQuery): Promise<AdvertisingOverviewTrend> {
    return this.respond(this.advertisingOverviewStore.trend(query))
  }

  async adAttributionReconciliation(query: AdReconciliationQuery): Promise<AdAttributionReconciliation> {
    return this.respond(this.advertisingOverviewStore.reconciliation(query))
  }

  async adAttributionReconciliationCsv(query: AdReconciliationQuery): Promise<Blob> {
    return this.respond(this.advertisingOverviewStore.reconciliationCsv(query))
  }
  private get recoveryStore(): MockRecoveryStore {
    return new MockRecoveryStore(this.clock, () => this.tenant.currency)
  }

  async recoverableCheckoutsSummary(query: RecoveryQuery): Promise<RecoverableCheckoutsSummary> {
    return this.respond(this.recoveryStore.summary(query))
  }

  async recoverableCheckouts(query: RecoveryQuery): Promise<RecoverableCheckoutsList> {
    return this.respond(this.recoveryStore.list(query))
  }

  async recoverableCheckoutsCsv(query: RecoveryQuery): Promise<Blob> {
    return this.respond(this.recoveryStore.csv(query))
  }

  // --- the competitions list (wave 12) ---------------------------------------------

  private get competitionsStore(): MockCompetitionsStore {
    return new MockCompetitionsStore(this.dataset(), this.tenant.currency)
  }

  competitions(query: CompetitionsQuery): Promise<Envelope<CompetitionsList>> {
    const { data, period } = this.competitionsStore.list(query)
    return this.respond({ meta: { ...this.meta(period), currency: this.tenant.currency }, data })
  }

  competitionsCsv(query: CompetitionsQuery): Promise<CsvFile> {
    return this.respond(this.competitionsStore.csv(query))
  }

  // --- one competition's page (wave 15) ---------------------------------------------

  private competitionDetailCache: { key: string; store: MockCompetitionDetailStore } | null = null

  /** The demo's competition pages for this tenant and day: built once, as the customers store is, so its days are computed once. */
  private get competitionDetailStore(): MockCompetitionDetailStore {
    const key = `${this.tenant.id}|${toDateKey(this.clock())}`
    if (this.competitionDetailCache?.key !== key) {
      this.competitionDetailCache = { key, store: new MockCompetitionDetailStore(this.dataset(), this.tenant.currency) }
    }
    return this.competitionDetailCache.store
  }

  /** A demo answer, or the store's refusal (a competition the demo does not have is the API's 404), after the same latency. */
  private detailRead<T>(read: () => { data: T; period: PeriodInfo }): Promise<Envelope<T>> {
    try {
      const { data, period } = read()
      return this.respond({ meta: { ...this.meta(period), currency: this.tenant.currency }, data })
    } catch (error) {
      return this.respond(undefined).then(() => Promise.reject(error))
    }
  }

  competitionDetail(query: CompetitionDetailQuery & { granularity?: SalesGranularity }): Promise<Envelope<CompetitionDetail>> {
    return this.detailRead(() => this.competitionDetailStore.detail(query.filters, query.competitionId, query.granularity))
  }

  competitionFunnel(query: CompetitionDetailQuery): Promise<Envelope<CompetitionFunnel>> {
    return this.detailRead(() => this.competitionDetailStore.funnel(query.filters, query.competitionId))
  }

  competitionSources(query: CompetitionDetailQuery & { model?: AttributionModel }): Promise<Envelope<CompetitionSources>> {
    return this.detailRead(() =>
      this.competitionDetailStore.sources(query.filters, query.competitionId, query.model ?? DEFAULT_SOURCES_MODEL),
    )
  }

  competitionDevices(query: CompetitionDetailQuery): Promise<Envelope<CompetitionDevices>> {
    return this.detailRead(() => this.competitionDetailStore.devices(query.filters, query.competitionId))
  }

  competitionAdvertising(query: CompetitionDetailQuery): Promise<Envelope<CompetitionAdvertising>> {
    return this.detailRead(() => this.competitionDetailStore.advertising(query.filters, query.competitionId))
  }

  // --- the Revenue page (wave 18) ------------------------------------------------------

  private revenueCache: { key: string; mock: MockRevenue } | null = null

  /** The demo's revenue routes for this tenant and day: built once, so each day's figures are worked out once. */
  private get revenueMock(): MockRevenue {
    const key = `${this.tenant.id}|${toDateKey(this.clock())}`
    if (this.revenueCache?.key !== key) this.revenueCache = { key, mock: new MockRevenue(this.dataset(), this.tenant.currency) }
    return this.revenueCache.mock
  }

  /**
   * A revenue body read through the production mapper, under the demo's meta (its period
   * named as the API names it), or the demo's refusal (hours over a week is the API's 422)
   * after the same latency.
   */
  private revenueAnswer<Out extends { meta: RevenueMetaOut }, T>(filters: GlobalFilters, build: () => Out, map: (body: Out) => T): Promise<Envelope<T>> {
    let body: Out
    try {
      body = build()
    } catch (error) {
      return this.respond(undefined).then(() => Promise.reject(error))
    }
    const { period } = overviewMeta(body.meta, filters, this.clock(), { lastUpdatedAt: null, currency: null, coverage: null })
    return this.respond({ meta: { ...this.meta(period), currency: body.meta.currency }, data: map(body) })
  }

  revenueSummary(query: RevenueQuery): Promise<Envelope<RevenueSummary>> {
    return this.revenueAnswer(query.filters, () => this.revenueMock.summary(query), revenueSummaryFromWire)
  }

  revenueSeries(query: RevenueSeriesQuery): Promise<Envelope<RevenueSeries>> {
    return this.revenueAnswer(query.filters, () => this.revenueMock.trend(query), revenueSeriesFromWire)
  }

  revenueBreakdown(query: RevenueBreakdownQuery): Promise<Envelope<RevenueBreakdown>> {
    return this.revenueAnswer(query.filters, () => this.revenueMock.breakdown(query), revenueBreakdownFromWire)
  }

  revenueTiming(query: RevenueQuery): Promise<Envelope<RevenueTiming>> {
    return this.revenueAnswer(query.filters, () => this.revenueMock.timing(query), revenueTimingFromWire)
  }

  // --- the Revenue page's lifecycle, leakage and orders (wave 18 stage B) --------------

  /** The demo's wire body mapped as the HTTP client maps the API's, with the demo's meta. */
  private revenueLeakageAnswer<T>(filters: GlobalFilters, data: T): Promise<Envelope<T>> {
    const period = periodInfo(resolvePeriod(filters, this.clock()))
    return this.respond({ meta: { ...this.meta(period), currency: this.tenant.currency }, data })
  }

  revenueLifecycle(query: LifecycleQuery): Promise<Envelope<RevenueLifecycle>> {
    return this.revenueLeakageAnswer(query.filters, lifecycleFromWire(demoLifecycle(this.tenant, this.clock(), query)))
  }

  revenueLeakage(query: LeakageQuery): Promise<Envelope<RevenueLeakage>> {
    return this.revenueLeakageAnswer(query.filters, leakageFromWire(demoLeakage(this.tenant, this.clock(), query)))
  }

  revenueOrders(query: OrdersQuery): Promise<Envelope<RevenueOrders>> {
    return this.revenueLeakageAnswer(query.filters, ordersFromWire(demoOrders(this.tenant, this.clock(), query)))
  }

  revenueOrdersCsv(query: OrdersQuery): Promise<CsvFile> {
    return this.respond(demoOrdersCsv(this.tenant, this.clock(), query))
  }

  // --- the customers list and sign-ups (wave 13) ---------------------------------

  private customersCache: { key: string; store: MockCustomersStore } | null = null

  /** The demo's customers for this tenant and day: built once, since every request reads the same pool. */
  private get customersStore(): MockCustomersStore {
    const key = `${this.tenant.id}|${toDateKey(this.clock())}`
    if (this.customersCache?.key !== key) this.customersCache = { key, store: new MockCustomersStore(this.dataset(), this.tenant.currency) }
    return this.customersCache.store
  }

  /** The list route's answer, or the search's: one page, with the demo's meta in the tenant's currency. */
  private customersAnswer(query: CustomersQuery, search?: CustomerSearch): Promise<Envelope<CustomersList>> {
    const { data, period } = this.customersStore.list(query, search)
    return this.respond({ meta: { ...this.meta(period), currency: this.tenant.currency }, data })
  }

  customers(query: CustomersQuery): Promise<Envelope<CustomersList>> {
    return this.customersAnswer(query)
  }

  searchCustomers(query: CustomersQuery, search: CustomerSearch): Promise<Envelope<CustomersList>> {
    return this.customersAnswer(query, search)
  }

  customersCsv(query: CustomersQuery): Promise<CsvFile> {
    return this.respond(this.customersStore.csv(query))
  }

  async revealCustomer(customerId: string): Promise<CustomerReveal> {
    return this.respond(this.customersStore.reveal(customerId))
  }

  signups(filters: GlobalFilters): Promise<Envelope<SignupsReport>> {
    const { data, period } = demoSignups(this.dataset(), filters)
    return this.respond({ meta: this.meta(period), data })
  }

  // --- connector health (wave 9) -------------------------------------------------

  connectorWebsites(): Promise<CampaignWebsite[]> {
    return this.respond(this.campaignsStore().websites())
  }

  /** A 404 for a website that is not the active tenant's, as the API answers. */
  async websiteHealth(websiteId: string): Promise<WebsiteHealth> {
    const website = this.tenant.websites.find((candidate) => candidate.id === websiteId)
    if (!website) throw new ApiError(404, 'tenant_scope_error', 'resource not found in this tenant')
    return this.respond(demoWebsiteHealth(website.id, website.environment, this.clock()))
  }

}

// --- connector health: the demo's four websites, one of each status (wave 9) -----

/**
 * Across the demo tenants, each status once: 676's production site has the
 * failed reconcile the Overview alert names (`DEMO_CONNECTOR_FAILURE`), its
 * staging site has gone quiet, Northfield's site is delivering, and the neutral
 * demo's site has a connector installed but nothing sent yet. A website the
 * table does not name (a test's own fixture) is connected.
 */
const DEMO_WEBSITE_STATUS: Readonly<Record<string, WebsiteHealthStatus>> = {
  [DEMO_CONNECTOR_FAILURE.websiteId]: 'needs_attention',
  web_676_staging: 'stale',
  web_nf_prod: 'connected',
  web_demo: 'no_events_yet',
}

function demoWebsiteHealth(websiteId: string, environment: Environment, now: Date): WebsiteHealth {
  const ago = (minutes: number) => new Date(now.getTime() - minutes * 60_000).toISOString()
  const status = DEMO_WEBSITE_STATUS[websiteId] ?? 'connected'
  const lastRun = (finishedMinutesAgo: number, sent: number, skipped: number, failed: number) => ({
    lastReconcileStartedAt: ago(finishedMinutesAgo + 1),
    lastReconcileFinishedAt: ago(finishedMinutesAgo),
    lastReconcileWindowFrom: ago(finishedMinutesAgo + 60),
    lastReconcileWindowTo: ago(finishedMinutesAgo),
    lastReconcileSent: sent,
    lastReconcileSkipped: skipped,
    lastReconcileFailed: failed,
  })
  const report = {
    connectorVersion: '1.0.41',
    contractVersion: '1.0',
    lastReconcileErrorClass: null,
    missedRun: false,
    queueBacklog: 0,
    queueParked: 0,
    queueOldestAgeSeconds: null,
  }
  const fresh = { pageViews: ago(3), commerce: ago(18), entities: ago(52) }
  const base = { websiteId, environment, status, computedAt: now.toISOString() }
  switch (status) {
    case 'needs_attention': {
      const { sent, skipped, failed, errorClass, finishedMinutesAgo } = DEMO_CONNECTOR_FAILURE
      return {
        ...base,
        lastEventAt: fresh,
        quarantinedLast24h: 2,
        report: {
          ...report,
          ...lastRun(finishedMinutesAgo, sent, skipped, failed),
          reportedAt: ago(finishedMinutesAgo),
          lastReconcileErrorClass: errorClass,
          nextRunDueAt: ago(finishedMinutesAgo - 60),
          queueBacklog: failed,
          queueParked: failed,
          queueOldestAgeSeconds: finishedMinutesAgo * 60,
        },
      }
    }
    case 'stale':
      return {
        ...base,
        lastEventAt: { pageViews: ago(2 * 1440 + 180), commerce: ago(2 * 1440 + 300), entities: ago(3 * 1440) },
        quarantinedLast24h: 0,
        report: { ...report, ...lastRun(2 * 1440, 6, 0, 0), reportedAt: ago(2 * 1440), nextRunDueAt: ago(2 * 1440 - 60) },
      }
    case 'no_events_yet':
      return {
        ...base,
        lastEventAt: { pageViews: null, commerce: null, entities: null },
        quarantinedLast24h: 0,
        // Test connection on a fresh install: the connector has reported, but not run yet.
        report: {
          ...report,
          reportedAt: ago(25),
          lastReconcileStartedAt: null,
          lastReconcileFinishedAt: null,
          lastReconcileWindowFrom: null,
          lastReconcileWindowTo: null,
          lastReconcileSent: 0,
          lastReconcileSkipped: 0,
          lastReconcileFailed: 0,
          nextRunDueAt: ago(-35),
        },
      }
    case 'connected':
      return {
        ...base,
        lastEventAt: fresh,
        quarantinedLast24h: 0,
        report: { ...report, ...lastRun(7, 38, 1, 0), reportedAt: ago(7), nextRunDueAt: ago(-53) },
      }
  }
}

/**
 * Settings > Websites demo parity: the first website always shows one active
 * and one revoked credential, and a tenant with a second website shows an
 * active one there too, so the screen is never demonstrated against an empty
 * state.
 */
function seedDemoSiteCredentials(
  backend: MockSiteCredentialsBackend,
  websites: readonly { id: string; environment: SiteCredential['environment'] }[],
  now: Date,
): void {
  const [first, second] = websites
  if (!first) return
  const day = 24 * 60 * 60 * 1000
  const issuedAt = new Date(now.getTime() - 30 * day).toISOString()
  const oldIssuedAt = new Date(now.getTime() - 90 * day).toISOString()
  const revokedAt = new Date(now.getTime() - 10 * day).toISOString()
  backend.seed(first.id, [
    {
      id: 'sc_demo000001',
      website_id: first.id,
      environment: first.environment,
      label: 'WordPress connector',
      status: 'active',
      created_at: issuedAt,
      rotated_at: null,
      revoked_at: null,
      overlap_until: null,
    },
    {
      id: 'sc_demo000002',
      website_id: first.id,
      environment: first.environment,
      label: 'Old connector (rotated out)',
      status: 'revoked',
      created_at: oldIssuedAt,
      rotated_at: null,
      revoked_at: revokedAt,
      overlap_until: null,
    },
  ])
  if (second) {
    backend.seed(second.id, [
      {
        id: 'sc_demo000003',
        website_id: second.id,
        environment: second.environment,
        label: 'WordPress connector',
        status: 'active',
        created_at: issuedAt,
        rotated_at: null,
        revoked_at: null,
        overlap_until: null,
      },
    ])
  }
}
